import { useContext, useMemo, useState, VFC } from "react";
// Libraries
import { useMutation, useLazyQuery } from "@apollo/client";
import { useParams } from "react-router-dom";
// Utils
import { GET_MY_ANIMALS_LIST } from "api/MyLivestock/Animal/queries";
import {
  GetAnimalsList,
  GetAnimalsListVariables,
  GetAnimalsList_animalsExtended_animals,
} from "api/MyLivestock/Animal/types/GetAnimalsList";
import { ASSIGN_ANIMALS_TO_GROUP } from "api/MyLivestock/Groups/mutations";
import { CommonContext } from "config/commonProvider";
import { TableOptionsContext } from "config/tableOptionsProvider";
import { EDIT_ANIMALS_IN_GROUP_TYPES } from "constants/Groups";
import { TABLE_IDS } from "constants/Interface";
import { getGroupAnimalsTableTitle } from "helpers/myLivestock";
import { getBUFromLocalStorage } from "helpers/storage";
import { useAnimals, useHasFeature, useGetGroupById } from "hooks";
// Components
import AnimalsInGroup from "pages/MyLivestock/Groups/AnimalsInGroup";
import { AnimalFiltersBlock, EditModeButtons, NotEditModeButtons } from "components/MyLivestock";
import { Flex, LoadingOverlay, Spacer, Title } from "components";
// Resources
import styles from "./AssignAnimalsToGroup.module.scss";

interface AssignAnimalsToGroup {
  changeEditMode: (newValue: string | null) => void;
  isEdited?: string | null;
  isBatch?: boolean;
  type?: string;
}

const AssignAnimalsToGroup: VFC<AssignAnimalsToGroup> = ({
  isEdited,
  changeEditMode,
  isBatch = false,
  type = "group",
}) => {
  const { groupId } = useParams<LivestockGroupPageParams>();

  const { showNotification, resetAnimalsFilter } = useContext(CommonContext);

  const { getTableOptions } = useContext(TableOptionsContext);
  const groupTableOptions = getTableOptions<GetAnimalsList_animalsExtended_animals>(TABLE_IDS.GROUP_ANIMALS);
  const groupTableSelectedAnimalIds = groupTableOptions?.selectedRows?.map((animal) => animal.id) || [];

  const [error, setError] = useState<string | null>(null);

  const { data, refetch } = useGetGroupById(groupId);
  const group = data?.group;

  const [getAnimals, { data: allAnimals, loading: allAnimalsLoading }] = useLazyQuery<
    GetAnimalsList,
    GetAnimalsListVariables
  >(GET_MY_ANIMALS_LIST);
  const animalsNotInGroup = allAnimals?.animalsExtended?.animals;

  const [assignAnimals, { loading: assignAnimalsLoading }] = useMutation(ASSIGN_ANIMALS_TO_GROUP);
  const isShowAnimalsFilter = useHasFeature("ANIMALS_FILTER");

  const animalsList = useMemo(
    () =>
      animalsNotInGroup && isEdited === EDIT_ANIMALS_IN_GROUP_TYPES.ADD_TO_GROUP
        ? animalsNotInGroup.filter((animal) => {
            if (!animal) return false;

            const isAnimalIncorrectSpecies = group?.animalType && animal.animalType?.id !== group?.animalType?.id;
            if (isAnimalIncorrectSpecies) return false;

            const isAnimalInThisGroup = animal.group && animal?.group.id === group?.id;
            if (isAnimalInThisGroup) return false;

            return true;
          })
        : group?.animals,
    [animalsNotInGroup, isEdited, group],
  );

  // @ts-ignore
  const { data: resultedAnimals, animalsCount } = useAnimals(animalsList);

  const animalsCountLabel = `Showing ${resultedAnimals?.length} of ${animalsCount}`;
  const animalsToExport: string[] = resultedAnimals?.map((item) => item?.id) || [];

  const assignAnimalsToGroup = async (): Promise<void> => {
    setError(null);

    try {
      const {
        data: {
          moveAnimalsToGroup: { errors },
        },
      } = await assignAnimals({
        variables: {
          input: {
            group: group?.id,
            animals: groupTableSelectedAnimalIds,
            date: new Date(),
          },
        },
      });

      if (errors) {
        setError(errors[0].message);

        showNotification({
          variant: "error",
          message: "Error updating group.",
        });
      } else {
        showNotification({
          message: "Group successfully updated.",
        });

        resetAnimalsFilter();

        changeEditMode(null);

        await refetch();
      }
    } catch (e) {
      showNotification({
        variant: "error",
        message: `Error updating group.`,
      });

      setError(`Error updating group.`);
    }
  };

  const fetchAllAnimalsList = async (): Promise<void> => {
    getAnimals({ variables: { businessUnitId: getBUFromLocalStorage() } });

    resetAnimalsFilter();
  };

  const onCancel = (): void => {
    setError(null);

    changeEditMode(null);

    resetAnimalsFilter();
  };

  return (
    <>
      <Flex container containerJustifyContent="space-between">
        <Flex item itemGutter>
          <Title secondary>{getGroupAnimalsTableTitle(isEdited, groupTableSelectedAnimalIds?.length, type)}</Title>
        </Flex>

        <Flex item itemGutter>
          {isEdited ? (
            <EditModeButtons
              type={type}
              onCancel={onCancel}
              assignAnimalsToGroup={assignAnimalsToGroup}
              isButtonDisabled={groupTableSelectedAnimalIds?.length === 0}
            />
          ) : (
            <NotEditModeButtons
              isBatch={isBatch}
              isEdited={isEdited}
              changeEditMode={changeEditMode}
              fetchAllAnimalsList={fetchAllAnimalsList}
              isButtonVisible={!!group && !!group.animals && group.animals.length > 0}
              animals={resultedAnimals}
            />
          )}
        </Flex>
      </Flex>

      {error ? <p className={styles.error}>{error}</p> : null}

      {group ? (
        <>
          {allAnimalsLoading ? (
            <LoadingOverlay />
          ) : (
            <>
              {assignAnimalsLoading ? <LoadingOverlay /> : null}

              <Spacer baselineHeight={3} />

              {isShowAnimalsFilter ? (
                <>
                  <AnimalFiltersBlock
                    excludedFields={{ animalTypeId: true, groups: true, ageAtDelivery: true, weightAtDelivery: true }}
                    animalsCountLabel={animalsCountLabel}
                    animalsToExport={!isEdited ? animalsToExport : undefined}
                    countAnimalsToExport={resultedAnimals?.length || 0}
                    tableId={TABLE_IDS.GROUP_ANIMALS}
                  />

                  <Spacer baselineHeight={1} />
                </>
              ) : null}

              <AnimalsInGroup group={group} isEdited={!!isEdited} searchedAnimals={resultedAnimals} />
            </>
          )}
        </>
      ) : null}
    </>
  );
};

export default AssignAnimalsToGroup;
